import { ArrowUpRight, Check, Building2 } from "lucide-react";
import { Reveal, Eyebrow } from "@/lib/motion";
import { COACHING_AREAS, CORPORATE_TOPICS, WORKSHOPS, IMG, LINKS } from "@/data/site";

/* ---------------- Coaching ---------------- */
export const Coaching = () => (
  <section id="coaching" className="relative py-24 md:py-32 scroll-mt-20 overflow-hidden" data-testid="coaching-section">
    <div className="absolute inset-0 bg-ink-soft" />
    <div className="absolute top-0 right-0 w-[40rem] h-[40rem] rounded-full bg-brand/10 blur-[130px] pointer-events-none" />
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Reveal><Eyebrow>Coaching</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight">Get Personal Guidance With AI</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 text-lg text-slate-400 max-w-lg">For learners who want a personalized roadmap, feedback and practical implementation support.</p>
          </Reveal>
          <Reveal delay={0.3}>
            <a href={LINKS.connect} target="_blank" rel="noopener noreferrer" data-testid="coaching-book-btn" className="mt-8 group inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-light text-white font-semibold px-8 py-4 transition-colors">
              Book a Coaching Call <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <div className="rounded-3xl border border-white/10 glass p-8">
            <p className="text-sm text-slate-400 uppercase tracking-wider font-semibold">Coaching areas</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {COACHING_AREAS.map((a) => (
                <div key={a} className="flex items-center gap-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand/15 text-brand-light"><Check className="h-3.5 w-3.5" /></span>
                  <span className="text-white text-sm">{a}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Workshops ---------------- */
export const Workshops = () => (
  <section id="workshops" className="relative py-24 md:py-32 scroll-mt-20" data-testid="workshops-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="max-w-2xl">
        <Reveal><Eyebrow>Workshops</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight">Learn. Build. Experiment.</h2>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {WORKSHOPS.map((w, i) => (
          <Reveal key={w.title} delay={(i % 3) * 0.08}>
            <div data-testid={`workshop-card-${i}`} className="group h-full rounded-3xl border border-white/5 bg-ink-surface p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-brand/40">
              <div className="flex items-start justify-between">
                <span className="font-heading text-3xl font-bold text-white/10">{String(i + 1).padStart(2, "0")}</span>
                {w.soon && <span className="rounded-full bg-white/5 text-slate-400 text-xs font-semibold px-3 py-1">Coming Soon</span>}
              </div>
              <h3 className="mt-6 font-heading text-xl font-semibold text-white group-hover:text-brand-light transition-colors">{w.title}</h3>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Corporate Training ---------------- */
export const Corporate = () => (
  <section className="relative py-16" data-testid="corporate-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <div className="relative rounded-[2rem] overflow-hidden border border-white/10">
          <img src={IMG.workshop} alt="Practical AI team training" className="absolute inset-0 h-full w-full object-cover opacity-25" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-ink/70" />
          <div className="relative p-8 md:p-14 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/15 text-brand-light"><Building2 className="h-6 w-6" /></span>
              <h2 className="mt-6 font-heading text-3xl md:text-4xl font-semibold text-white tracking-tight">Bring Practical AI Skills to Your Team</h2>
              <p className="mt-4 text-slate-400 max-w-lg">SuhaniEdge can provide practical AI education for organizations, teams and educators.</p>
              <a href={LINKS.connect} target="_blank" rel="noopener noreferrer" data-testid="corporate-enquire-btn" className="mt-8 group inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-light text-white font-semibold px-8 py-4 transition-colors">
                Enquire About Training <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {CORPORATE_TOPICS.map((t) => (
                <div key={t} className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-sm text-white font-medium">{t}</div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
